import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon, { EcoIconName } from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import { useGoBack } from "../../../hooks/useGoBack";

import type { RootState } from "../../../store/store";
import { LOGINPAGE } from "../../auth/routes";
import { HOMEPAGE } from "../../home/routes";

import { STOREPAGE } from "../../store/routes";
import { SLOTS_MANAGER_PAGE } from "../../scheduling/routes";
import { FREELANCER_CONTROL } from "../../freelancer-control/routes";
import {
  STORE_EVALUATIONS_ADMIN_PAGE,
  STORE_QUALITY_DASHBOARD_PAGE,
  STORE_COVERAGE_PAGE
} from "../../store-evaluations/routes";
import { NR1_LINKS_ADMIN_PAGE, NR1_DASHBOARD_PAGE } from "../../nr1/routes";
import { QUIZ_HUB_PAGE } from "../../quiz/routes";

import { UPDATE_PRESENCE_AND_VALUES_TABLE_PAGE } from "../../update-presence-table/routes";
import { EVALUATION_FORMS_ADMIN_PAGE } from "../../evaluations/routes";
import { REPORTS_PAGE } from "../../reports/routes";

// Modal de diária por UF (o mesmo do admin)
import ShiftBaseConfigModal from "../../../components/modal/shift-base-config/ShiftBaseConfigModal";

import "./OperacoesPage.css";

type MenuKey =
  | "freelancer"
  | "loja"
  | "agenda"
  | "relatorios"
  | "presenca-valores"
  | "formulario-perguntas"
  | "diaria-uf"
  | "avaliacoes-lojas"
  | "qualidade-lojas"
  | "cobertura-semanal"
  | "nr1-links"
  | "nr1-dashboard"
  | "quiz";

type MenuCard = {
  key: MenuKey;
  title: string;
  description: string;
  icon: EcoIconName;
  onClick: () => void;
};

type MenuSection = {
  id: string;
  title: string;
  /**
   * Seção visível mas inativa: os cards não respondem ao clique e o título
   * leva este aviso entre parênteses.
   */
  disabledNote?: string;
  cards: MenuCard[];
};

const OperacoesPage: React.FC = () => {
  const navigate = useNavigate();
  const goBack = useGoBack(HOMEPAGE());

  const token = useSelector((state: RootState) => state.auth.accessToken);

  const [isShiftBaseConfigModalOpen, setIsShiftBaseConfigModalOpen] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("Você precisa estar logado.");
      navigate(LOGINPAGE(), { replace: true });
    }
  }, [token, navigate]);

  const handleOpenShiftBaseConfig = useCallback(() => {
    if (!token) {
      toast.error("Sessão inválida. Faça login novamente.");
      navigate(LOGINPAGE(), { replace: true });
      return;
    }
    setIsShiftBaseConfigModalOpen(true);
  }, [token, navigate]);

  const sections: MenuSection[] = useMemo(
    () => [
      {
        id: "freelancers",
        title: "Freelancers e escalas",
        // Decisão do usuário (01/10/2026): as features de freelancer não estão
        // em uso por enquanto. Os atalhos ficam à vista, mas não navegam; para
        // reativar, basta remover esta linha.
        disabledNote: "fora de uso por enquanto",
        cards: [
          {
            // O sub-hub `/freelancer` (Cadastrar/Consultar/Alterar/Excluir) saiu: o
            // cadastro administrativo mudou para /administracao/usuarios e o que
            // resta aqui é a operação do dia a dia. O card abre o controle direto.
            key: "freelancer",
            title: "Freelancer",
            description: "Controle diário, presença e agenda.",
            icon: "userPlus",
            onClick: () => navigate(FREELANCER_CONTROL()),
          },
          {
            key: "agenda",
            title: "Agenda",
            description: "Criar, editar e visualizar horários.",
            icon: "calendar",
            onClick: () => navigate(SLOTS_MANAGER_PAGE()),
          },
          {
            key: "presenca-valores",
            title: "Presença e valores",
            description: "Consultar e alterar presença e valores.",
            icon: "dollar",
            onClick: () => navigate(UPDATE_PRESENCE_AND_VALUES_TABLE_PAGE()),
          },
          {
            key: "diaria-uf",
            title: "Diária por UF",
            description: "Valor base da diária por estado.",
            icon: "mapPin",
            onClick: handleOpenShiftBaseConfig,
          },
        ],
      },
      {
        id: "lojas",
        title: "Lojas e qualidade",
        cards: [
          {
            key: "loja",
            title: "Loja",
            description: "Cadastrar, consultar e alterar lojas.",
            icon: "store",
            onClick: () => navigate(STOREPAGE()),
          },
          {
            key: "avaliacoes-lojas",
            title: "Avaliações de loja",
            description: "Questionários de auditoria respondidos.",
            icon: "checkSquare",
            onClick: () => navigate(STORE_EVALUATIONS_ADMIN_PAGE()),
          },
          {
            key: "qualidade-lojas",
            title: "Qualidade de loja",
            description: "Conformidade e notas por loja.",
            icon: "barChart",
            onClick: () => navigate(STORE_QUALITY_DASHBOARD_PAGE()),
          },
          {
            key: "cobertura-semanal",
            title: "Cobertura semanal",
            description: "Lojas visitadas por cada supervisor de campo.",
            icon: "calendar",
            onClick: () => navigate(STORE_COVERAGE_PAGE()),
          },
          {
            key: "formulario-perguntas",
            title: "Formulários de perguntas",
            description: "Criar e gerenciar formulários de avaliação.",
            icon: "helpCircle",
            onClick: () => navigate(EVALUATION_FORMS_ADMIN_PAGE()),
          },
        ],
      },
      {
        id: "nr1",
        title: "NR1 — riscos psicossociais",
        cards: [
          {
            key: "nr1-links",
            title: "Links anônimos",
            description: "Links de uso único para pesquisa por loja.",
            icon: "link",
            onClick: () => navigate(NR1_LINKS_ADMIN_PAGE()),
          },
          {
            key: "nr1-dashboard",
            title: "Riscos por loja",
            description: "Painel com as respostas anônimas.",
            icon: "pieChart",
            onClick: () => navigate(NR1_DASHBOARD_PAGE()),
          },
        ],
      },
      {
        id: "quiz",
        title: "Quiz",
        cards: [
          {
            key: "quiz",
            title: "Quiz para supervisores",
            description: "Perguntas com gabarito, link com prazo e notas.",
            icon: "clipboardCheck",
            onClick: () => navigate(QUIZ_HUB_PAGE()),
          },
        ],
      },
      {
        id: "relatorios",
        title: "Relatórios",
        cards: [
          {
            key: "relatorios",
            title: "Gerar relatórios",
            description: "Exportar relatórios por período e loja.",
            icon: "download",
            onClick: () => navigate(REPORTS_PAGE()),
          },
        ],
      },
    ],
    [navigate, handleOpenShiftBaseConfig],
  );

  if (!token) return null;

  return (
    <div className="ecoOps">
      <main className="ecoOps__main">
        <EcoPageHeader
          crumbs={[{ label: "Início", onClick: () => navigate(HOMEPAGE()) }, { label: "Operações" }]}
          title="Operações"
          subtitle="Freelancers, lojas, escalas, avaliações e relatórios do dia a dia."
          onBack={goBack}
          backLabel="Voltar ao início"
        />

        {sections.map((section) => (
          <section
            key={section.id}
            className="ecoOps__section"
            aria-labelledby={`ecoOps-${section.id}`}
          >
            <h2 id={`ecoOps-${section.id}`} className="ecoOps__sectionTitle">
              {section.title}
              {section.disabledNote ? (
                <span className="ecoOps__sectionNote"> ({section.disabledNote})</span>
              ) : null}
            </h2>

            <div className="ecoOps__grid">
              {section.cards.map((card) => (
                <button
                  key={card.key}
                  type="button"
                  className="ecoOps__card"
                  onClick={section.disabledNote ? undefined : card.onClick}
                  disabled={Boolean(section.disabledNote)}
                >
                  <span className="ecoOps__cardIcon">
                    <EcoIcon name={card.icon} size={20} strokeWidth={1.8} />
                  </span>
                  <span className="ecoOps__cardText">
                    <span className="ecoOps__cardTitle">{card.title}</span>
                    <span className="ecoOps__cardDesc">{card.description}</span>
                  </span>
                  <EcoIcon name="chevronRight" className="ecoOps__cardChevron" />
                </button>
              ))}
            </div>
          </section>
        ))}

        <ShiftBaseConfigModal
          isOpen={isShiftBaseConfigModalOpen}
          onClose={() => setIsShiftBaseConfigModalOpen(false)}
          token={token}
          onSaved={() => toast.success("Diária salva com sucesso")}
        />
      </main>
    </div>
  );
};

export default OperacoesPage;
